import { ItemInstance } from '@headless-tree/core';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, IconFormIcons, Plus, Target } from 'shared/assets/icons';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { ListSectionGoalHeader } from 'shared/components/ui/list-section-goal-header';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { goalInboxID } from 'shared/types/orderable-section';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './goal-group-header.style';

export type GoalGroupHeaderProps = {
  item: ItemInstance<InternalTreeItem>;
  isFirst?: boolean;
  onAddGoal?: () => void;
};

// The parent-goal section header for the group-by-goal projects list — the same
// goal summary used by the habits list, with the tree's collapse chevron.
export const GoalGroupHeader: React.FC<GoalGroupHeaderProps> = ({
  item,
  isFirst,
  onAddGoal,
}) => {
  const { t } = useTranslation();
  const onPremium = useOpenPremiumDialog();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();

  const goalId = item.getItemData().sourceId;

  const goal = useMemo(
    () => goals.find(({ id }) => id === goalId),
    [goals, goalId],
  );

  const goalColor = useMemo(
    () =>
      goal?.id
        ? getLinkedItemColor(goal.id, { goals, lifeAreas, links }) ?? undefined
        : undefined,
    [goal?.id, goals, lifeAreas, links],
  );

  const goalName =
    goalId === goalInboxID
      ? t('general.sections.goals.other.title')
      : goal?.name ?? '';

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    item.isExpanded() ? item.collapse() : item.expand();
  };

  const handleAddGoal = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddGoal?.();
  };

  return (
    <Styled.Container $isFirst={isFirst}>
      <ListSectionGoalHeader
        id={goalId}
        name={goalName}
        image={goal?.image}
        imagePlaceholderIcon={
          goalId === goalInboxID
            ? Target
            : goal?.iconName
              ? IconFormIcons[goal.iconName]
              : undefined
        }
        color={goalColor}
        onPremium={onPremium}
        isFrozen={!!goal?.frozenAt}
        isLink={goalId !== goalInboxID}
      />
      <Styled.ActionsContainer>
        <Styled.HoverActions>
          {onAddGoal && (
            <IconButton
              icon={Plus}
              onClick={handleAddGoal}
              color={Colors.Default}
            />
          )}
        </Styled.HoverActions>
        <IconButton
          icon={ChevronLeft}
          onClick={toggleOpen}
          rotation={item.isExpanded() ? -90 : 0}
          color={Colors.Default}
        />
      </Styled.ActionsContainer>
    </Styled.Container>
  );
};
